import React from "react";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { useTypewriter } from "@/hooks/use-typewriter";
import type { Evidence } from "@/types/evidences";
import { reformatCitations } from "@/views/chat/helpers";
import { PromptResult } from "./businessReportOutcomeContent";
import { useUpdateBusinessReportOutcomeResultMutation } from "../businessReports.api";
import { PencilIcon } from "lucide-react";
import { Loader } from "@/components/ui/loader";
import { useToggle } from "react-use";
import { BusinessReportOutcomeRowRetry } from "@/modules/snapshots/outcome-details/businessReportOutcomeRowRetry";
import TopBarProgress from "react-topbar-progress-indicator";
import { toast } from "react-toastify";
import { clsx } from "clsx";

interface Props {
  content: string;
  evidences: Evidence[];
  promptResult: PromptResult;
  processing: boolean;
  onPromptTypewriterEnded: (prompt: PromptResult) => void;
  onScroll: () => void;
  businessReportOutcomeResultId: string;
  businessReportOutcomeId: string;
}

const Component: React.FC<Props> = ({
  processing,
  content,
  evidences,
  promptResult,
  businessReportOutcomeResultId,
  businessReportOutcomeId,
  onPromptTypewriterEnded,
  onScroll,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const typedContend = useTypewriter(processing ? content : "", 5);
  const [isEdit, toggleIsEdit] = useToggle(false);
  const [updateBusinessReportOutcomeResult, { isLoading }] = useUpdateBusinessReportOutcomeResultMutation();

  React.useEffect(() => {
    if (processing && typedContend === content) {
      onPromptTypewriterEnded(promptResult);
    } else if (processing && typedContend !== content) {
      onScroll();
    }
  }, [typedContend, content, promptResult, onPromptTypewriterEnded, processing, onScroll]);

  const handleSaveResult = async (result: string) => {
    try {
      result = result.replace(/<br>/g, "\n");
      await updateBusinessReportOutcomeResult({
        resultId: businessReportOutcomeResultId,
        id: businessReportOutcomeId,
        data: {
          result,
        },
      }).unwrap();
      toast.success("Saved");
      toggleIsEdit(false);
    } catch (error) {
      toast.error("Error while saving");
      console.error(error);
    }
  };

  return (
    <>
      {isLoading && <TopBarProgress />}
      <div className="mt-6 text-2xl font-normal">
        <div className="flex items-center">
          {promptResult && promptResult.order + 1}. {promptResult.name}
          {!processing && (
            <>
              <BusinessReportOutcomeRowRetry
                businessReportOutcomeId={businessReportOutcomeId}
                businessReportOutcomeResultId={businessReportOutcomeResultId}
                setIsProcessing={setIsProcessing}
              />
              <div onClick={toggleIsEdit} className="ml-2 cursor-pointer">
                <PencilIcon className="h-3 w-3" />
              </div>
            </>
          )}
        </div>
      </div>
      {isProcessing ? (
        <Loader />
      ) : isEdit ? (
        <div
          contentEditable={isEdit}
          className={clsx({
            "border-2 border-gray-500": isEdit,
          })}
          onBlur={t => {
            handleSaveResult(t.currentTarget.innerHTML);
          }}
        >
          {content}
        </div>
      ) : (
        <MarkdownRenderer
          content={reformatCitations(processing ? typedContend : content)}
          evidences={evidences}
          processCitations={true}
        />
      )}
      <hr className="my-4 h-px border-0 bg-gray-200 dark:bg-gray-700" />
    </>
  );
};

export const BusinessReportOutcomeRow = React.memo(Component);
